import {
  hasOfferMarker,
  OFFER_MARKER_REGEX,
  type OfferKind,
  stripOfferMarkers,
} from '@revelio/core/global-ai-assistant/offer-marker';

export {
  hasOfferMarker,
  OFFER_MARKER_REGEX,
  stripOfferMarkers,
  type OfferKind,
};

export type OfferSegment =
  | { type: 'text'; text: string }
  | {
      type: 'offer';
      offer: OfferKind;
      text: string;
      /** The marker's machine target (`offer:report:<reportType>`), if any. */
      target?: string;
    };

/**
 * Split assistant narration into ordered segments so the FE can render offer
 * markers as styled trigger spans and everything else as plain text. Never
 * throws; unrecognized bracket/paren text passes through verbatim.
 */
export function parseOfferSegments(text: string): OfferSegment[] {
  const re = new RegExp(OFFER_MARKER_REGEX.source, 'g');
  const segments: OfferSegment[] = [];
  let lastIndex = 0;
  for (const match of text.matchAll(re)) {
    const start = match.index ?? 0;
    if (start > lastIndex) {
      segments.push({ type: 'text', text: text.slice(lastIndex, start) });
    }
    segments.push({
      type: 'offer',
      offer: match[2] as OfferKind,
      // match[1]/[2] are always present when the regex matches; the `?? ''`
      // satisfies strict indexed-access typing (noUncheckedIndexedAccess).
      text: match[1] ?? '',
      // Only report triggers resolve by a target; a stray one on another
      // kind is ignored rather than changing how that trigger resolves.
      ...(match[3] && match[2] === 'report' ? { target: match[3] } : {}),
    });
    lastIndex = start + match[0].length;
  }
  if (lastIndex < text.length) {
    segments.push({ type: 'text', text: text.slice(lastIndex) });
  }
  // Collapse to a single text segment when nothing matched (keeps the no-marker
  // path allocation-free and the equality simple for callers/tests).
  return segments.length === 0 ? [{ type: 'text', text }] : segments;
}

/**
 * Where an UNTARGETED report marker sits among the untargeted report markers
 * of the same text, plus the report types the text's typed markers name.
 * Older transcripts, and a retried offer_report, write `[Name](offer:report)`
 * with no type. Position only; the words are never read.
 */
export function untargetedReportPosition(
  text: string,
  segmentIndex: number
): { ordinal: number; count: number; namedTypes: string[] } | undefined {
  const segments = parseOfferSegments(text.trim());
  const isUntargetedReport = (segment: OfferSegment) =>
    segment.type === 'offer' && segment.offer === 'report' && !segment.target;
  const target = segments[segmentIndex];
  if (!target || !isUntargetedReport(target)) {
    return undefined;
  }
  return {
    ordinal: segments.slice(0, segmentIndex).filter(isUntargetedReport).length,
    count: segments.filter(isUntargetedReport).length,
    namedTypes: segments.flatMap((segment) =>
      segment.type === 'offer' && segment.offer === 'report' && segment.target
        ? [segment.target]
        : []
    ),
  };
}

/**
 * The offer an inline report trigger opens: by the report type its marker
 * names (`[Name](offer:report:<reportType>)`), never by its visible words.
 *
 * An untargeted marker opens by position among the offers no typed marker
 * names, and only when there is exactly one untargeted marker per such offer,
 * since offer_report asked for markers in reportTypes order. Any miss returns
 * undefined and the trigger renders as plain text.
 */
export function matchReportOfferByMarker<T extends { reportType: string }>(
  offers: readonly T[],
  target: string | undefined,
  untargetedPosition?: { ordinal: number; count: number; namedTypes: string[] }
): T | undefined {
  if (target) {
    return offers.find((offer) => offer.reportType === target);
  }
  if (!untargetedPosition) {
    return undefined;
  }
  const unnamed = offers.filter(
    (offer) => !untargetedPosition.namedTypes.includes(offer.reportType)
  );
  return untargetedPosition.count === unnamed.length
    ? unnamed[untargetedPosition.ordinal]
    : undefined;
}
